<script setup>
import { useMetrics } from "@/composables/use.metrics";
import { getEventSource } from "@/helpers/getEventSource";
import { errorHandler } from "@/lib/errorHandler";
import locations from "@/lib/locations.json";
import dayjs from "dayjs";
import { computed, defineEmits, defineProps } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  events: {
    type: Array,
    default: () => [],
  }
});

const { eventTypes, hitType, hit } = useMetrics();

const router = useRouter();
const store = useStore();

const user = computed(() => store.getters.userData);


const filteredEvents = computed(() => {
  const chronics = props.events.find(({ title }) => title.toLocaleLowerCase().includes('хроники'));
  const russia = props.events.find(({ title }) => title.toLocaleLowerCase().includes('выставка-форум'));

  return [chronics, russia];
});

const emits = defineEmits(["update:modelValue", "next"]);

function onClose() {
  emits('update:modelValue', false);
}


function getEventDescription(description) {
  if (!description.toLowerCase().includes('международная') && !description.toLowerCase().includes('россия')) {
    return description;
  }

  return 'Посетите уникальные экспозиции международной выставки-форума «России» в метавселенной. Выбирайте удобное время и отправляйтесь на прогулку!'
}

function loadAltPreview(event) {
  event.target.src = require("@/assets/new-icons/landing/test-data/test-1.png");
}


async function bookedEvent(event) {
  hit(eventTypes.FIREWORK, hitType.BOOK_EVENT, event);

  if (props.format === "landing") {
    return await router.push({ name: "mainPage" });
  }

  if (event?.type === "record") {
    if (!event?.translationUrl) {
      return errorHandler({ name: "videoRecordingDoesNotExist" });
    }
    const { state } = await store.dispatch(
      "checkVideoRecordingAvailable",
      event.translationUrl,
    );
    if (!state) {
      errorHandler({ name: "videoRecordingDoesNotExist" });
    } else {
      await router.push({ name: "videoPage", params: { id: event?.id } });
    }
    return;
  }
  if (event?.type === "broadcast") {
    const el = document.createElement("a");

    el.href = event?.eventPlace;
    el.target = "_blank";
    el.click();

    return;
  }

  if (getEventStatus(event) !== "ok") {
    errorHandler({ name: "eventStatus", status: getEventStatus(event) });
    return;
  }

  if (event?.type === "lecture" && user.value.role !== "lecture") {
    if (!event?.placement) {
      return router.push({
        name: "pickPlacement",
        params: { eventId: event?.id },
      });
    } else {
      sessionStorage.setItem("eventPlacement", JSON.stringify(event.placement));
    }
  }

  // if (user.value.role !== "guide") {
  if (event.eventSlots && event.eventSlots?.ticketQuantityOnDay) {
    if (event.eventPlace === locations.russian.place) {
      const { origin } = window.location;
      const url = new URL(`/cabinet/date-select/${event?.id}`, origin).toString();

      return window.open(url, '_self');
    }

    return router.push({
      name: "dateSelect",
      params: {
        id: event?.id,
      },
    });
  }

  if (event.participantsCount >= event.participantsLimit) {
    return errorHandler({ name: "participantsLimit" });
  }

  if (["dedicatedTour", "excursion"]?.includes(event?.type)) {
    return await router.push({ name: "lobbyPage", params: { id: event?.id } });
  }
  // }

  sessionStorage.setItem("eventId", event?.id);
  sessionStorage.setItem("internalId", event.internalId);
  sessionStorage.setItem("eventType", event.type);
  return await router.push({ name: "gamePage" });

}


function getEventStatus(event) {
  if (["record", "broadcast"].includes(event?.type)) return "ok";

  if (dayjs().unix() > dayjs(event.eventDateEnd).unix()) return "end";

  if (dayjs().unix() < dayjs(event.eventDateStart).unix()) return "notStarted";

  return "ok";
}
</script>

<template>
  <Transition name="fade">
    <div v-if="modelValue" class="shell">
      <div class="modal">
        <div class="modal-content">
          <div class="caption">
            <div class="caption-block">
              <p class="title">
                Посетите другие мероприятия МетаВДНХ!
              </p>
              <p class="text" v-if="false">
                Дальше вы можете перейти к другим мероприятиям МЕТАВднх
              </p>
            </div>

            <button class="close" @click="onClose">
                <svg class="close__img" width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clip-path="url(#clip0_7509_946)">
                    <rect width="21" height="21" rx="10.5" fill="white" />
                    <path d="M10.5 0C4.65 0 0 4.65 0 10.5C0 16.35 4.65 21 10.5 21C16.35 21 21 16.35 21 10.5C21 4.65 16.35 0 10.5 0ZM14.55 15.75L10.5 11.7L6.45 15.75L5.25 14.55L9.3 10.5L5.25 6.45L6.45 5.25L10.5 9.3L14.55 5.25L15.75 6.45L11.7 10.5L15.75 14.55L14.55 15.75Z" fill="#5D6C85" />
                  </g>
                  <defs>
                    <clipPath id="clip0_7509_946">
                      <rect width="21" height="21" rx="10.5" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
            </button>
          </div>

          <div class="cards">
            <div v-for="event, index in filteredEvents" :key="index" class="card">
              <div class="card-photo">
                <img
                  :src="getEventSource(event)"
                  alt=""
                  class="card-photo__content"
                  @error="loadAltPreview"
                />
              </div>
              <div class="card-fill">
                <p class="card__title">
                  {{ event.title }}
                </p>
                <p class="card__text">
                  {{ getEventDescription(event.description) }}
                </p>
              </div>
              <div class="card-info">
                <div class="card-info__element">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M9.78125 8.55766V3.2187H8.21875V9C8.21875 9.27443 8.36274 9.52873 8.59806 9.66992L12.393 11.9468L13.1969 10.607L9.78125 8.55766Z"
                      fill="#828282" />
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M9 0.09375C4.08123 0.09375 0.09375 4.08123 0.09375 9C0.09375 13.9188 4.08123 17.9063 9 17.9063C13.9188 17.9063 17.9063 13.9188 17.9063 9C17.9063 4.08123 13.9188 0.09375 9 0.09375ZM1.65625 9C1.65625 4.94418 4.94418 1.65625 9 1.65625C13.0558 1.65625 16.3438 4.94418 16.3438 9C16.3438 13.0558 13.0558 16.3438 9 16.3438C4.94418 16.3438 1.65625 13.0558 1.65625 9Z"
                      fill="#828282" />
                  </svg>
                  <p class="card-info__text">{{ event.eventSlots.duration }} мин.</p>
                </div>
                <div class="card-info__element">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                    <g clip-path="url(#clip0_5086_7825)">
                      <path
                        d="M9 3.20459C5.13636 3.20459 1.81364 5.60004 0.5 9.00004C1.81364 12.4 5.13636 14.7955 9 14.7955C12.8636 14.7955 16.1864 12.4 17.5 9.00004C16.1864 5.60004 12.8636 3.20459 9 3.20459ZM9 12.8637C6.83636 12.8637 5.13636 11.1637 5.13636 9.00004C5.13636 6.83641 6.83636 5.13641 9 5.13641C11.1636 5.13641 12.8636 6.83641 12.8636 9.00004C12.8636 11.1637 11.1636 12.8637 9 12.8637ZM9 6.68186C7.68636 6.68186 6.68182 7.68641 6.68182 9.00004C6.68182 10.3137 7.68636 11.3182 9 11.3182C10.3136 11.3182 11.3182 10.3137 11.3182 9.00004C11.3182 7.68641 10.3136 6.68186 9 6.68186Z"
                        fill="#828282" />
                    </g>
                    <defs>
                      <clipPath id="clip0_5086_7825">
                        <rect
                          width="17"
                          height="17"
                          fill="white"
                          transform="translate(0.5 0.5)" />
                      </clipPath>
                    </defs>
                  </svg>
                  <p class="card-info__text">{{ event.eventSlots.ticketQuantityOnDay }} чел.</p>
                </div>
              </div>
              <button class="link" @click="bookedEvent(event)">Записаться</button>
            </div>
            <!-- <div class="card">
              <div class="card-photo">
                <img
                  src="@/assets/9may/walk.png"
                  alt=""
                  class="card-photo__content" />
              </div>
              <div class="card-fill">
                <p class="card__title">Интерактивная прогулка по МетаВДНХ</p>
                <p class="card__text">
                  Посмотрите крупнейший <br />
                  экспозиционный, музейный и рекреационный комплекс в мире, не
                  выходя из дома!
                </p>
              </div>
              <div class="card-info">
                <div class="card-info__element">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M9.78125 8.55766V3.2187H8.21875V9C8.21875 9.27443 8.36274 9.52873 8.59806 9.66992L12.393 11.9468L13.1969 10.607L9.78125 8.55766Z"
                      fill="#828282" />
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M9 0.09375C4.08123 0.09375 0.09375 4.08123 0.09375 9C0.09375 13.9188 4.08123 17.9063 9 17.9063C13.9188 17.9063 17.9063 13.9188 17.9063 9C17.9063 4.08123 13.9188 0.09375 9 0.09375ZM1.65625 9C1.65625 4.94418 4.94418 1.65625 9 1.65625C13.0558 1.65625 16.3438 4.94418 16.3438 9C16.3438 13.0558 13.0558 16.3438 9 16.3438C4.94418 16.3438 1.65625 13.0558 1.65625 9Z"
                      fill="#828282" />
                  </svg>
                  <p class="card-info__text">15 мин.</p>
                </div>
                <div class="card-info__element">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                    <g clip-path="url(#clip0_5086_7825)">
                      <path
                        d="M9 3.20459C5.13636 3.20459 1.81364 5.60004 0.5 9.00004C1.81364 12.4 5.13636 14.7955 9 14.7955C12.8636 14.7955 16.1864 12.4 17.5 9.00004C16.1864 5.60004 12.8636 3.20459 9 3.20459ZM9 12.8637C6.83636 12.8637 5.13636 11.1637 5.13636 9.00004C5.13636 6.83641 6.83636 5.13641 9 5.13641C11.1636 5.13641 12.8636 6.83641 12.8636 9.00004C12.8636 11.1637 11.1636 12.8637 9 12.8637ZM9 6.68186C7.68636 6.68186 6.68182 7.68641 6.68182 9.00004C6.68182 10.3137 7.68636 11.3182 9 11.3182C10.3136 11.3182 11.3182 10.3137 11.3182 9.00004C11.3182 7.68641 10.3136 6.68186 9 6.68186Z"
                        fill="#828282" />
                    </g>
                    <defs>
                      <clipPath id="clip0_5086_7825">
                        <rect
                          width="17"
                          height="17"
                          fill="white"
                          transform="translate(0.5 0.5)" />
                      </clipPath>
                    </defs>
                  </svg>
                  <p class="card-info__text">10 чел.</p>
                </div>
              </div>
              <button class="link">Записаться</button>
            </div> -->
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped lang="scss">
.shell {
  background: #00288c;
  z-index: 1000000;
}

.modal {
  padding: 0;
  max-width: 872px;
  border-radius: 40px;
  pointer-events: all;
  backdrop-filter: blur(8px);
  background: rgba(157, 196, 248, 0.3);
  padding: 24px;
  width: 100%;

  &-content {
    background: #fff;
    border-radius: 24px;
    padding: 24px;
    overflow: hidden;
  }
    @media (max-width:1000px) {
    padding: 16px;
    &-content {
      padding: 16px;
    }
  }
}

.title {
  font-weight: 700;
  font-size: 24px;
  line-height: 130%;
  letter-spacing: 0.01em;
  color: #000;
}
.text {
  margin-top: 8px;
  font-weight: 400;
  font-size: 14px;
  line-height: 143%;
  letter-spacing: -0.01em;
  color: rgba(0, 0, 0, 0.6);
}
.caption {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.close {
  display: flex;
  background: none;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  transition: all 0.5s ease-in-out;

  &:hover {
    opacity: 0.5;
  }

  img {
    width: 100%;
  }
}
.cards {
  margin-top: 16px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  @media (max-width:786px){
      grid-template-columns: repeat(1, 1fr);
  }
}
.card {
  border-radius: 32px;
  padding: 16px 16px 20px 16px;
  background: #f0f2f6;
  display: flex;
  flex-direction: column;
  &-photo {
    position: relative;
    padding-bottom: 45.21%;
    width: 100%;
    &__content {
      position: absolute;
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 28px;
      left: 0;
      top: 0;
    }
  }
  &-fill {
    padding: 10px 20px 0 10px;
  }
  &__title {
    font-weight: 700;
    font-size: 20px;
    line-height: 120%;
    color: #000;
  }
  &__text {
    margin-top: 8px;
    font-size: 15px;
    line-height: 133%;
    color: rgba(0, 0, 0, 0.8);
    margin-bottom: 8px;
  }
  &-info {
    margin-top: auto;
    display: flex;
    justify-content: space-around;
    margin-bottom: 16px;
    &__element {
      display: flex;
      align-items: center;
      gap: 9px;
    }
    &__text {
      font-weight: 400;
      font-size: 16px;
      line-height: 87%;
      color: #828282;
    }
  }
  .link {
    width: 100%;
    padding: 13px;
    font-size: 16px;
    line-height: 140%;
    justify-content: center;
  }
  @media (max-width:1000px){
    padding: 14px;
    &__title{
      font-size: 16px;
    }
    &__text{
      font-size: 13px;
    }
    .link{
          padding:0.625rem 1rem;
    font-size: 0.88rem;
    }
  }
}
.link-default {
  margin-top: 16px;
}

@media (max-width: 1300px) {
  .title {
    font-size: 20px;
  }
  .text {
    font-size: 16px;
  }
}
</style>
